import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { staffAttApi, errMsg } from '@/services/staffAttendanceApi';

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const Result = ({ r }) => (
  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm space-y-1" data-testid="essl-upload-result">
    <div><b>{r.file_name}</b>{r.device ? ` · ${r.device}` : ''} — {r.rows} punches ({r.date_from} → {r.date_to})</div>
    {r.monthly_report && <div className="text-emerald-800" data-testid="essl-upload-monthly">Monthly Status Report — first In + last Out per day</div>}
    <div className="text-emerald-800"><b data-testid="essl-upload-new">{r.new}</b> new · <span data-testid="essl-upload-updated">{r.updated || 0}</span> times updated · <span data-testid="essl-upload-dupes">{r.duplicates}</span> already uploaded{r.bad_lines ? ` · ${r.bad_lines} unreadable lines skipped` : ''}</div>
    {r.unmatched_count > 0 && <div className="text-amber-700" data-testid="essl-upload-unmatched">{r.unmatched_count} eSSL code(s) not in Staff Master: {(r.unmatched || r.unmatched_codes.map((code) => ({ code }))).slice(0, 15).map((u) => (u.name && u.name !== u.code ? `${u.code} (${u.name})` : u.code)).join(', ')}{r.unmatched_count > 15 ? ' …' : ''} — set it as the staff's <b>eSSL Code</b> (or Staff ID) in Staff Master.</div>}
    {r.merge && <div className="text-emerald-800" data-testid="essl-upload-merge">Final times worked out for closed days: <b>{r.merge.changed_days}</b> staff-day(s) updated — {r.merge.filled_days} filled (no app punch), {r.merge.in_earlier} In set earlier, {r.merge.out_later} Out set later, {r.merge.out_added} missing Out filled{r.merge.skipped_today ? ' · today left for tomorrow' : ''}</div>}
  </div>
);

/** HR: upload the log downloaded from each eSSL machine (attlog .dat / .txt / .csv / .xlsx). */
export const EsslUpload = ({ onUploaded }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [device, setDevice] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const loadHistory = () => staffAttApi.esslUploads().then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const upload = async () => {
    setBusy(true);
    try {
      const r = await staffAttApi.esslUpload(file, device.trim());
      setResult(r); setFile(null); if (fileRef.current) fileRef.current.value = '';
      toast.success(`${r.new} new eSSL punches stored`);
      loadHistory(); onUploaded(r);
    } catch (e) { toast.error(errMsg(e, 'Could not read the eSSL log')); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="essl-upload">
      <div className="font-semibold">Upload machine log</div>
      <p className="text-xs text-gray-500">Each morning, upload the attendance log downloaded from every eSSL machine (USB <b>attlog .dat</b>, .txt, .csv or .xlsx with Employee Code + Date-Time) or the eTimeTrackLite <b>Monthly Status Report</b> (.xls / .xlsx, all company sheets). Staff are matched by eSSL Code, else by Staff ID. Re-uploading is safe — duplicates are skipped and a newer Monthly report updates that day's In / Out. Punches before the day-close time count for the previous day (OT). After each upload the final In (earlier of app / eSSL) and Out (later of app / eSSL) are set for the closed days in the file.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input ref={fileRef} type="file" accept=".dat,.txt,.csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-[300px] bg-white" data-testid="essl-file-input" />
        <Input value={device} onChange={(e) => setDevice(e.target.value)} placeholder="Machine name (optional)" className="w-[200px] bg-white" data-testid="essl-device-input" />
        <Button onClick={upload} disabled={!file || busy} className="bg-amber-600 hover:bg-amber-700" data-testid="essl-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload
        </Button>
      </div>
      {result && <Result r={result} />}
      {history.length > 0 && (
        <div className="text-xs text-gray-500 space-y-0.5" data-testid="essl-upload-history">
          <div className="font-semibold text-gray-600">Recent uploads</div>
          {history.slice(0, 5).map((h) => (
            <div key={h.upload_id} data-testid={`essl-history-${h.upload_id}`}>{fmtAt(h.uploaded_at)} · {h.file_name}{h.device ? ` (${h.device})` : ''} · {h.new} new / {h.rows} · {h.date_from}→{h.date_to} · {h.uploaded_by_name}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EsslUpload;
